import { useEffect, useRef, useState } from 'react';

const POLL_INTERVAL = 5 * 60 * 1000; // 5 minutos
const VERSION_URL = '/version.json';

/**
 * useVersionCheck — detecta quando há nova versão da app publicada.
 *
 * Funcionamento:
 *   - Ao montar, busca /version.json e guarda o buildId como "boot version"
 *   - A cada POLL_INTERVAL e quando a aba volta ao foco, busca de novo
 *   - Se o buildId atual diferir do bootVersion, sinaliza updateAvailable=true
 *
 * Para o cliente em cache antigo: depois do primeiro deploy com este código,
 * todas as builds futuras serão detectadas. O fetch usa cache:no-store +
 * query string para evitar caching agressivo do CDN.
 */
export default function useVersionCheck() {
  const [updateAvailable, setUpdateAvailable] = useState(false);
  const bootVersionRef = useRef(null);

  useEffect(() => {
    let cancelled = false;

    const fetchVersion = async () => {
      try {
        const res = await fetch(`${VERSION_URL}?t=${Date.now()}`, {
          cache: 'no-store',
        });
        if (!res.ok) return;
        const data = await res.json();
        const id = data?.buildId;
        if (cancelled || !id) return;
        if (bootVersionRef.current === null) {
          bootVersionRef.current = id;
        } else if (bootVersionRef.current !== id) {
          setUpdateAvailable(true);
        }
      } catch {
        // silencioso — sem internet ou bloqueio de CORS
      }
    };

    fetchVersion();
    const interval = setInterval(fetchVersion, POLL_INTERVAL);

    const onVisibility = () => {
      if (document.visibilityState === 'visible') fetchVersion();
    };
    document.addEventListener('visibilitychange', onVisibility);

    return () => {
      cancelled = true;
      clearInterval(interval);
      document.removeEventListener('visibilitychange', onVisibility);
    };
  }, []);

  const reload = () => {
    // Limpa caches do Service Worker se existirem (PWA)
    if ('caches' in window) {
      caches.keys().then(keys => keys.forEach(k => caches.delete(k))).catch(() => {});
    }
    window.location.reload();
  };

  return { updateAvailable, reload };
}
